import { holdPropsInUtc, PushNotification } from "@repo/helpers";
import {
	FilterItem,
	KeyedArray,
	SerializedFile,
	Complaint,
	ComplaintCustomerAction,
	ComplaintUtility,
	ActionTakenCategories,
} from "@repo/models";
import { useCallback, useEffect, useState } from "react";

import { useComplaintContext, useRepContext } from "../contexts/editableItemContext";
import { useSettingsQuery } from "../helpers/queryHooks";
import { useEntityId } from "../helpers/urlHooks";
import * as Queries from "./queries";
import { useRepIncidentApi } from "./repIncidentApi";
import {
	useFilterItemQueryWrapper,
	useFilterItemV2QueryWrapper,
	useQueryWrapper,
	useRepPortalApi,
} from "./repPortalApi";

export function useComplaintApi() {
	const { Get, Post, Delete } = useRepPortalApi();
	const ComplaintsApi = useRepPortalApi("/MVC/CustomerIncidents");
	const GridFiltersApi = useRepPortalApi("/MVC/GridFilters");
	const LocationsApi = useRepPortalApi("/MVC/Locations");
	const { assignToQaAnalyst } = useRepIncidentApi();
	const [settings] = useSettingsQuery();

	const initialize = useCallback(function (complaint: Complaint) {
		if (settings.timesInLocal) holdPropsInUtc(complaint, "incidentDate", "enrollmentDate");
		return complaint;
	}, []);

	const checkDuplicates = useCallback(
		function (complaint: Complaint) {
			return Post<Complaint[]>("CheckDuplicates", null, complaint);
		},
		[Post],
	);

	const saveComplaint = useCallback(
		function (complaint: Complaint, email: boolean) {
			return Post<number, any>("SaveComplaint", null, { complaint, email });
		},
		[Post],
	);

	const saveComplaintDocs = useCallback(
		function (id: number, documents: SerializedFile[]) {
			return Post<number, any>("SaveComplaintDocs", { id }, JSON.stringify(documents));
		},
		[Post],
	);

	const loadComplaint = useCallback(
		function (complaintId: number) {
			return Get<Complaint>("GetComplaint", { complaintId }).then(initialize);
		},
		[Get, initialize],
	);

	const getComplaints = useCallback(
		async function (repId: number, complaintId: number) {
			const complaints = await Get<Complaint[]>("Complaints", {
				representativeId: repId,
				complaintId,
			});
			return complaints.map(initialize);
		},
		[Get, initialize],
	);

	const loadComplaints = useCallback(
		function (repId: number, complaintId: number) {
			return async function () {
				try {
					const complaints = await getComplaints(repId, complaintId);
					const keyedItems = new KeyedArray<Complaint>(complaints, (b) => b.id);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve complaints");
					return defaultComplaintArr();
				}
			};
		},
		[getComplaints],
	);

	const saveCustomerActionTaken = useCallback(
		function (customerAction: ComplaintCustomerAction, complaintId?: number) {
			return Post<number>("SaveCustomerActionTaken", null, {
				complaintId,
				customerAction,
			});
		},
		[Post],
	);

	const getCustomerActions = useCallback(
		function (complaintId: number) {
			if (!complaintId) return null;
			return Get<ComplaintCustomerAction[]>("CustomerActions", { complaintId });
		},
		[Get],
	);

	const loadCustomerActions = useCallback(
		function (complaintId: number) {
			return async function () {
				if (!complaintId) return defaultComplaintCustomerActionArr();
				try {
					const actions = await getCustomerActions(complaintId);
					const keyedItems = new KeyedArray<ComplaintCustomerAction>(actions, (h) => h.id);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve background checks");
					return defaultComplaintCustomerActionArr();
				}
			};
		},
		[getComplaints],
	);

	const deleteCustomerAction = useCallback(
		function (customerActionId: number) {
			return Delete<number>("DeleteCustomerActionTaken", customerActionId);
		},
		[Delete],
	);

	const assignToAnalyst = assignToQaAnalyst("Complaints");

	const attachEnrollment = useCallback(
		function (complaintId: number, enrollmentId: string) {
			return ComplaintsApi.Post<number>("AttachEnrollment", {
				incidentId: complaintId,
				enrollmentId,
			});
		},
		[ComplaintsApi.Post],
	);

	const getUtilities = useCallback(
		function (repId: number, complaintId: number) {
			return ComplaintsApi.Get<ComplaintUtility[]>("GetUtilities", {
				repId,
				complaintId,
			});
		},
		[ComplaintsApi.Get],
	);

	const getComplaintTypes = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetComplaintTypesDyn");
		},
		[ComplaintsApi.Get],
	);

	const getComplaintSources = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetComplaintSourcesDyn");
		},
		[ComplaintsApi.Get],
	);

	const getEnrollmentSources = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetEnrollmentSourcesDyn");
		},
		[ComplaintsApi.Get],
	);

	const getStaffingPartners = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetStaffingPartnersDyn");
		},
		[ComplaintsApi.Get],
	);

	const getReportingTags = useCallback(
		function (brandId?: number) {
			return ComplaintsApi.Get<FilterItem[]>("GetReportingTagsDyn", {
				brandId,
			});
		},
		[ComplaintsApi.Get],
	);

	const getRootCauses = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetRootCausesDyn");
		},
		[ComplaintsApi.Get],
	);

	const getLocationPartners = useCallback(
		function (brandId?: number) {
			return LocationsApi.Get<FilterItem[]>("GetLocationPartnersDyn", {
				brandId,
				onlyWithLocations: true,
			});
		},
		[LocationsApi.Get],
	);

	const getBrands = useCallback(
		function () {
			return GridFiltersApi.Get<FilterItem[]>("GetBrandsDyn");
		},
		[GridFiltersApi.Get],
	);

	const getTelesalesBrands = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetTelesalesBrandsDyn");
		},
		[ComplaintsApi.Get],
	);

	const getEnrollments = useCallback(
		function (repId?: number) {
			return Get<FilterItem[]>("RepEnrollments", { repId });
		},
		[Get],
	);

	const getReps = useCallback(
		function (channelId?: number, repTypes?: number[]) {
			return GridFiltersApi.Get<FilterItem[]>("GetRepsDyn", {
				channelId,
				onlyActiveReps: false,
				repTypes,
			});
		},
		[GridFiltersApi.Get],
	);

	const getManagers = useCallback(
		function () {
			return Get<FilterItem[]>("GetRealtorManagers");
		},
		[Get],
	);

	const getComplaintFYIReasons = useCallback(
		function () {
			return ComplaintsApi.Get<FilterItem[]>("GetComplaintFYIReasonsDyn");
		},
		[ComplaintsApi.Get],
	);

	const getCustomerActionCategories = useCallback(
		function () {
			return ComplaintsApi.Get<ActionTakenCategories[]>("GetCustomerActionTakenCategoriesDyn");
		},
		[ComplaintsApi.Get],
	);

	const findAgent = useCallback(
		function (manualAgentId: string, manualRepName: string) {
			manualAgentId = manualAgentId ?? "";
			manualRepName = manualRepName ?? "";
			return ComplaintsApi.Get<any>("FindRep", {
				manualAgentId,
				manualRepName,
			}).defaultIfDev({});
		},
		[ComplaintsApi.Get],
	);

	const reassignAgent = useCallback(
		function (incidentId: number, repId: string) {
			return ComplaintsApi.Post<any>("Reassign", {
				incidentId,
				repId,
			}).defaultIfDev({});
		},
		[ComplaintsApi.Post],
	);

	const findEnrollment = useCallback(
		function (complaintId: number, customerAccountNumber: string, esid: string) {
			customerAccountNumber = customerAccountNumber ?? "";
			esid = esid ?? "";
			return ComplaintsApi.Get<any>("FindEnrollment", {
				complaintId,
				customerAccountNumber,
				esid,
			});
		},
		[ComplaintsApi.Get],
	);

	const assignToRepresentative = useCallback(
		function (complaintId: number, repId: number) {
			return ComplaintsApi.Post<number>("Reassign", {
				incidentId: complaintId,
				repId,
			});
		},
		[ComplaintsApi.Post],
	);

	return {
		saveComplaint,
		saveComplaintDocs,
		loadComplaint,
		loadComplaints,
		saveCustomerActionTaken,
		loadCustomerActions,
		deleteCustomerAction,
		assignToAnalyst,
		attachEnrollment,
		getUtilities,
		getComplaintTypes,
		getComplaintSources,
		getEnrollmentSources,
		getStaffingPartners,
		getReportingTags,
		getRootCauses,
		getLocationPartners,
		getBrands,
		getTelesalesBrands,
		getEnrollments,
		getReps,
		getManagers,
		getComplaintFYIReasons,
		getCustomerActionCategories,
		findAgent,
		reassignAgent,
		findEnrollment,
		assignToRepresentative,
		checkDuplicates,
	};
}

const defaultComplaintArr = () => new KeyedArray<Complaint>([], (b) => b.id);
const defaultComplaintCustomerActionArr = () => new KeyedArray<ComplaintCustomerAction>([], (b) => b.id);

export function useComplaintQueries() {
	const { repId, rep } = useRepContext();
	const { item: complaint } = useComplaintContext();
	const brandId = rep.brandId > 0 ? rep.brandId : (complaint?.manualBrandId ?? 0);

	const {
		loadComplaints,
		getComplaintTypes,
		getComplaintSources,
		getEnrollmentSources,
		getStaffingPartners,
		getComplaintFYIReasons,
		getReportingTags,
		getRootCauses,
		getLocationPartners,
		getBrands,
		getTelesalesBrands,
		getEnrollments,
		getReps,
		getManagers,
		getUtilities,
		loadCustomerActions,
		getCustomerActionCategories,
	} = useComplaintApi();

	const useComplaintQuery = () => {
		const entityId = useEntityId();
		const [instanceId, setInstanceId] = useState<number>(null);

		useEffect(() => {
			if (instanceId === null && entityId !== 0) setInstanceId(entityId);
		}, [entityId]);

		return useQueryWrapper(
			Queries.COMPLAINTS,
			[repId, instanceId, entityId, loadComplaints],
			loadComplaints(repId, entityId),
		);
	};

	const useComplaintTypesQuery = () => useFilterItemV2QueryWrapper(Queries.COMPLAINT.TYPES, [], getComplaintTypes);

	const useComplaintTypeLevelsQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.LEVELS, [], getComplaintTypes, null, "level");

	const useComplaintSourcesQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.SOURCES, [getComplaintSources], getComplaintSources);

	const useEnrollmentSourcesQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.ENROLLMENT_SOURCES, [getEnrollmentSources], getEnrollmentSources);

	const useStaffingPartnersQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.STAFFING_PARTNERS, [getStaffingPartners], getStaffingPartners);

	const useFYIReasonsQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.FYI_REASONS, [getComplaintFYIReasons], getComplaintFYIReasons);

	const useReportingTagsQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.REPORTING_TAGS, [getReportingTags, brandId], () =>
			getReportingTags(brandId),
		);

	const useRootCausesQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.ROOT_CAUSES, [getRootCauses], () => getRootCauses());

	const useLocationPartnersQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.LOCATION_PARTNERS, [getLocationPartners, brandId], () =>
			getLocationPartners(brandId),
		);

	const useBrandsQuery = () => useFilterItemV2QueryWrapper(Queries.COMPLAINT.BRANDS, [getBrands], getBrands);
	const useTelesalesBrandsQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.TELSALES_BRANDS, [getTelesalesBrands], getTelesalesBrands);

	const useEnrollmentsQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.ENROLLMENTS, [getEnrollments, repId], () =>
			getEnrollments(repId),
		);

	const useRepsQuery = (args?: { channelId?: number; repTypes?: number[] }) =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.REPS, [getReps, args?.channelId, args?.repTypes], () =>
			getReps(args?.channelId, args?.repTypes),
		);

	const useManagersQuery = () => useFilterItemV2QueryWrapper(Queries.COMPLAINT.MANAGERS, [getManagers], getManagers);

	const useUtilitiesQuery = (complaintId: number) =>
		useFilterItemQueryWrapper<ComplaintUtility>(
			Queries.COMPLAINT.UTILITIES,
			[getUtilities, repId, complaintId],
			() => getUtilities(repId, !repId ? complaintId : null),
		);

	const useCustomerActionQuery = (complaintId: number) =>
		useQueryWrapper(Queries.COMPLAINT.CUSTOMER_ACTIONS, [loadCustomerActions], loadCustomerActions(complaintId));

	const useCustomerActionCategoriesQuery = () =>
		useFilterItemQueryWrapper<ActionTakenCategories>(
			Queries.COMPLAINT.CUSTOMER_ACTION_CATEGORIES,
			[getCustomerActionCategories],
			getCustomerActionCategories,
			{ placeholderData: EmptyKeyedArray },
		);

	return {
		useComplaintQuery,
		useComplaintTypesQuery,
		useComplaintTypeLevelsQuery,
		useComplaintSourcesQuery,
		useEnrollmentSourcesQuery,
		useStaffingPartnersQuery,
		useFYIReasonsQuery,
		useReportingTagsQuery,
		useRootCausesQuery,
		useLocationPartnersQuery,
		useBrandsQuery,
		useTelesalesBrandsQuery,
		useEnrollmentsQuery,
		useRepsQuery,
		useManagersQuery,
		useUtilitiesQuery,
		useCustomerActionQuery,
		useCustomerActionCategoriesQuery,
	};
}

const EmptyKeyedArray = new KeyedArray([], (f) => f.id);
